class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;
    this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() { return this.input?.value.trim() || ''; }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.close(true);
      this.dispatchEvent(new CustomEvent('glx:predictive-results', { bubbles: true, detail: { term: '', handles: [] } }));
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.dispatchEvent(new CustomEvent('glx:predictive-results', { bubbles: true, detail: { term: '', handles: [] } }));
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) this.onChange();
    else if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(this.searchTerm);
  }

  onFocusOut() {
    setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;
    const matches = currentButtonText.match(new RegExp(previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'));
    if (matches && matches.length > 1) return;
    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (this.closest('.header__search')) return;
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter((element) => element.offsetParent !== null);
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;
    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
      i++;
    }
    if (this.statusElement) this.statusElement.textContent = '';
    if (!moveUp && selectedElement) activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    else if (moveUp) activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input?.setAttribute('aria-activedescendant', activeElement.id || '');
  }

  selectOption() {
    if (this.closest('.header__search')) return;
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const requestedTerm = searchTerm.trim();
    if (!requestedTerm) return;
    const queryKey = requestedTerm.toLowerCase().replace(/\s+/g, '-');

    this.abortController.abort();
    this.abortController = new AbortController();
    const controller = this.abortController;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      if (requestedTerm === this.searchTerm) this.renderSearchResults(this.cachedResults[queryKey], requestedTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(requestedTerm)}&section_id=predictive-search`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search request failed with ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (controller.signal.aborted || requestedTerm !== this.searchTerm) return;
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search response was missing its section.');
        const resultsMarkup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => { instance.cachedResults[queryKey] = resultsMarkup; });
        this.renderSearchResults(resultsMarkup, requestedTerm);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        console.error('[Glowinex search] Predictive search failed.', error);
        if (requestedTerm !== this.searchTerm) return;
        this.removeAttribute('loading');
        this.close();
        this.setLiveRegionText('Search is temporarily unavailable. Please try again.');
        this.dispatchEvent(new CustomEvent('glx:predictive-results', { bubbles: true, detail: { term: requestedTerm, handles: [] } }));
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    setTimeout(() => this.statusElement?.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(resultsMarkup, requestedTerm = this.searchTerm) {
    if (requestedTerm !== this.searchTerm || !this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();

    const handles = Array.from(this.querySelectorAll('.predictive-search a[href*="/products/"]')).map((a) => {
      try { return new URL(a.href, window.location.origin).pathname.split('/products/')[1]?.split('/')[0] || ''; }
      catch (_) { return ''; }
    }).filter(Boolean);
    this.dispatchEvent(new CustomEvent('glx:predictive-results', { bubbles: true, detail: { term: requestedTerm, handles } }));
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) this.setLiveRegionText(count.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults || !this.input) return;
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input?.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
